import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  AddressInput,
  Link,
} from '@shopmuchmore/design-system';
import { CheckCircle2, ArrowRight, MapPin, Plus } from 'lucide-react';
import { ShippingAddressItem } from '../../shipping/components/ShippingAddressItem';
import { useLocationPicker } from '../hooks/useLocationPicker';

export function LocationPickerModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const {
    lastGpsAddress,
    isLoadingGPS,
    userCountryCode,
    isAuthenticated,
    savedAddresses,
    isLoadingAddress,
    isDeletingAddress,
    useCurrentLocation,
    selectAddress,
    selectSavedAddress,
    deleteSavedAddress,
    setDefaultAddress,
  } = useLocationPicker();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-full sm:w-[425px] sm:max-w-[425px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Choose your location</SheetTitle>
          <SheetDescription>
            Set your location to see localized results and merchants near you.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6 py-4 px-4">
          <div>
            <AddressInput
              value={undefined}
              autocomplete={true}
              onSelectAddress={(data) => selectAddress(data, () => onOpenChange(false))}
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
              label="Enter your address"
              countries={['gb']}
            />
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or choose an option
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {userCountryCode === null || userCountryCode === 'GB' || userCountryCode === 'UK' ? (
              <button
                type="button"
                onClick={() => useCurrentLocation(() => onOpenChange(false))}
                disabled={isLoadingGPS}
                className="flex items-start gap-3 w-full p-3 hover:bg-accent/50 rounded-lg transition-colors text-left bg-card cursor-pointer disabled:cursor-not-allowed"
              >
                <div className="mt-0.5 text-primary">
                  <MapPin className="size-5" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="font-semibold text-primary text-[15px]">Use your current location</span>
                  {lastGpsAddress ? (
                    <span className="text-[13px] text-muted-foreground line-clamp-1">{lastGpsAddress}</span>
                  ) : isLoadingGPS ? (
                    <span className="text-[13px] text-muted-foreground">Locating...</span>
                  ) : null}
                </div>
              </button>
            ) : null}

            {isAuthenticated ? (
              <>
                {isLoadingAddress ? (
                  <div className="flex items-center justify-center p-4">
                    <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  </div>
                ) : (
                  <>
                    <Link
                      href="/account/addresses/new"
                      leadingIcon={<Plus className="size-5" />}
                      onClick={() => onOpenChange(false)}
                    >
                      Add New Address
                    </Link>
                    {savedAddresses.map((address: any) => {
                      return (
                        <div
                          key={address.shipping_slug || address.id}
                          onClick={() => selectSavedAddress(address, () => onOpenChange(false))}
                          className="cursor-pointer w-full hover:opacity-80 transition-opacity"
                        >
                          <ShippingAddressItem
                            address={address}
                            isDeleting={isDeletingAddress}
                            onDelete={(id) => deleteSavedAddress(id)}
                            onEdit={() => {
                              onOpenChange(false);
                              navigate(`/account/addresses/${address.shipping_slug || address.id}`);
                            }}
                            onSetDefault={() => setDefaultAddress(address.shipping_slug || address.id)}
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between border border-border rounded-lg p-3 w-full bg-card">
                <div className="flex items-center gap-2 text-muted-foreground text-[14px] font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Save new address
                </div>
                <Button
                  size="sm"
                  className="bg-[#1C763A] hover:bg-[#1C763A]/90 text-white px-4 h-9 font-semibold rounded-full border-none"
                  asChild
                >
                  <Link
                    href="/auth/login"
                    className="no-underline text-white hover:text-white"
                    onClick={() => onOpenChange(false)}
                  >
                    Login to save address <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
export default LocationPickerModal;
